import { appClient } from 'api/apiClient';
import { buildFileFormData } from 'api/v1Helpers';
import { normalizeBasket, toCreateBasketPayload, toUpdateBasketPayload, toApiBasketStatus, type BasketActivityUser } from 'types/baskets';
import { notify } from 'components/Common/Toast';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const addNewBasketData = async (parishId: string, form: Record<string, any>) => {
    const body = toCreateBasketPayload(form);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const response: any = await appClient.post(`parishes/${parishId}/baskets`, body);
    return normalizeBasket(response);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export const updateBasketDetail = async (parishId: string, basketId: string, form: Record<string, any>) => {
    const body = toUpdateBasketPayload(form);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const response: any = await appClient.patch(`parishes/${parishId}/baskets/${basketId}`, body);
    return normalizeBasket(response);
};

export const updateBasketImage = async (parishId: string, basketId: string, file: Blob) => {
    const formData = buildFileFormData(file);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const response: any = await appClient.put(`parishes/${parishId}/baskets/${basketId}/image`, formData);
    notify('Image updated successfully', 'success');
    return normalizeBasket(response);
};

export const removeBasketImage = async (parishId: string, basketId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const response: any = await appClient.delete(`parishes/${parishId}/baskets/${basketId}/image`);
    notify('Image removed successfully', 'success');
    return normalizeBasket(response);
};

export const deleteBasket = async (parishId: string, basketId: string) => {
    await appClient.delete(`parishes/${parishId}/baskets/${basketId}`);
    return { ok: true };
};

export const transitionBasketStatus = async (parishId: string, basketId: string, status: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const response: any = await appClient.post(`parishes/${parishId}/baskets/${basketId}/status`, {
        status: toApiBasketStatus(status),
    });
    return normalizeBasket(response);
};

export const fetchParishBasketDetail = async (parishId: string, basketId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const response: any = await appClient.get(`parishes/${parishId}/baskets/${basketId}`);
    return normalizeBasket(response);
};

type ContributionsPage = { items: BasketActivityUser[]; total: number; page?: number; pageSize?: number };

/** One request's worth of rows — the endpoint caps a page well below a busy basket's history. */
const contributionsPageSize = 100;
/** Hard stop so a wrong `total` from the server can never spin this into an unbounded fan-out. */
const maxContributionPages = 50;

/**
 * GET .../contributions (CLERK+) — SUCCEEDED + PARTIALLY_REFUNDED donations to this basket.
 *
 * Fetches the whole history, not just the first page: the Activity tab has no server-side
 * pagination and its CSV export is used to reconcile a basket's balance, so a silently truncated
 * first page reads as the basket's entire history. Page 1 reports the `total`, which makes the
 * remaining pages independent — they go out in parallel. If the page cap is hit first, the caller
 * is told rather than left with a short list that looks complete.
 */
export const fetchBasketContributions = async (parishId: string, basketId: string): Promise<ContributionsPage> => {
    const getPage = async (page: number) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
        const response: any = await appClient.get(`parishes/${parishId}/baskets/${basketId}/contributions`, {
            params: { page, pageSize: contributionsPageSize },
        });
        return (response || {}) as ContributionsPage;
    };

    const first = await getPage(1);
    const total = first.total || 0;
    const items = [...(first.items || [])];
    if (!items.length || items.length >= total) return { items, total };

    const pagesNeeded = Math.min(Math.ceil(total / contributionsPageSize), maxContributionPages);
    const rest = await Promise.all(Array.from({ length: pagesNeeded - 1 }, (_, index) => getPage(index + 2)));
    rest.forEach((page) => items.push(...(page.items || [])));

    if (total > items.length) {
        notify(`Showing the most recent ${items.length} of ${total} contributions.`, 'warning');
    }

    return { items, total };
};
